import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// 스크롤 진입 시 한 번만 애니메이션 트리거하는 훅 (Contact와 동일 패턴)
const useInView = (threshold = 0.2) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [threshold]);

  return [ref, inView];
};

const News = () => {
  const [newsList, setNewsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = window.innerWidth < 768 ? 6 : 9;
  const navigate = useNavigate();

  // 애니메이션용 훅 (상단 텍스트 / 뉴스 그리드 / 페이지네이션)
  const [textRef, textInView] = useInView(0.25);
  const [listRef, listInView] = useInView(0.2);
  const [pagerRef, pagerInView] = useInView(0.15);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(
          "https://www.medicode.co.kr:8445/api/get/board_list",
          {
            params: {
              cate: "news",
              page: currentPage,
              pageSize: itemsPerPage,
            },
          }
        );
        setNewsList(response.data.data);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        console.error("Error fetching news data:", error);
      }
    };
    fetchNews();
  }, [currentPage, itemsPerPage]);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  const handleFirstPage = () => {
    paginate(1);
  };

  const handleLastPage = () => {
    paginate(totalPages);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      paginate(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      paginate(currentPage - 1);
    }
  };

  const handleDetailClick = (idx) => {
    navigate(`/board/news/${idx}`);
  };

  return (
    <div className="news-wrap">
      {/* 상단 Hero – 애니메이션 없이 고정 */}
      <div className="news-top">
        <div className="page-tit">
          <div className="news-wrapper">
            <h3>메디코드의 소식을 들려드립니다.</h3>
            <h2>NEWS</h2>
          </div>
        </div>
        <div className="img-wrap"></div>
      </div>

      <div className="news-box">
        {/* 상단 텍스트 – 스크롤 진입 시 페이드업 */}
        <div
          className={`news-txt-wrap fade-section ${
            textInView ? "is-visible" : ""
          }`}
          ref={textRef}
        >
          <h2>
            Inside <span>MEDICODE</span>
          </h2>
          <p className="desc1">
            메디코드의 새로운 소식과 공지사항을 전해드립니다.
          </p>
          <p className="desc3">
            정기적으로 업로드되는 뉴스를 통해 메디코드를 가까이에서 만나보실 수
            있습니다.
          </p>
        </div>

        <div
          className={`news-divider fade-section ${
            textInView ? "is-visible" : ""
          }`}
        ></div>

        <div
          className={`news-us-input fade-section ${
            textInView ? "is-visible" : ""
          }`}
        >
          뉴스 목록
        </div>
      </div>

      {/* 뉴스 카드 그리드 – 아래에서 부드럽게 올라오게 */}
      <div
        className={`news-grid fade-section ${listInView ? "is-visible" : ""}`}
        ref={listRef}
      >
        {newsList.map((news, index) => (
          <div className="news-content-box" key={index}>
            <div className="image-container">
              <img
                src={`https://www.medicode.co.kr:8445/uploads/${news.img1}`}
                alt={news.title}
              />
              <div
                className="news-hover"
                onClick={() => handleDetailClick(news.idx)}
              >
                <p>상세보기</p>
              </div>
              <div className="news-meta-badge">
                {new Date(news.date).toISOString().split("T")[0]}
              </div>
            </div>
            <div className="news-content">
              <h3>{news.title}</h3>
              <p>{news.content.replace(/<[^>]+>/g, "")}</p>
              <p className="last-txt">
                {news.writer} &nbsp;
                {
                  new Date(news.date).toISOString().split("T")[0]
                } &nbsp;조회수: {news.hit}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* 페이지네이션도 살짝 딜레이 주고 올라오게 */}
      <div
        className={`pagination fade-section ${pagerInView ? "is-visible" : ""}`}
        ref={pagerRef}
      >
        <button onClick={handleFirstPage} disabled={currentPage === 1}>
          {"<<"}
        </button>
        <button onClick={handlePreviousPage} disabled={currentPage === 1}>
          {"<"}
        </button>
        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            onClick={() => paginate(i + 1)}
            className={currentPage === i + 1 ? "active" : ""}
          >
            {i + 1}
          </button>
        ))}
        <button onClick={handleNextPage} disabled={currentPage === totalPages}>
          {">"}
        </button>
        <button onClick={handleLastPage} disabled={currentPage === totalPages}>
          {">>"}
        </button>
      </div>
    </div>
  );
};

export default News;
